import { useState } from 'react'
import { CloudArrowDown } from '@phosphor-icons/react'
import { useNetworkStatus } from '@/api'
import { useFormat, useI18n } from '@/i18n'
import { downloadCaches, useDeviceInfo } from '@/offline'
import { Banner, Button, Card, CardBody, useToast } from '@/ui'

/** 'Update farmer list' with the last download age; warns when older than 24 h (F4.1). */
export function RosterCard({ compact = false }: { compact?: boolean }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const info = useDeviceInfo()
  const { online } = useNetworkStatus()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const update = async () => {
    setBusy(true)
    setError(null)
    try {
      await downloadCaches()
      toast.success(t('gate.roster.updated'))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardBody className="flex flex-col gap-2.5 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[14px] font-semibold">{t('device.rosterLabel')}</div>
            <div className="text-[13px] text-muted">
              {info.rosterDownloadedAt
                ? t('device.rosterAge', { when: fmt.relative(info.rosterDownloadedAt) })
                : t('device.rosterNever')}
            </div>
          </div>
          <Button
            variant="secondary"
            size={compact ? 'md' : 'touch'}
            loading={busy}
            disabled={!online}
            onClick={() => void update()}
            leadingIcon={<CloudArrowDown aria-hidden="true" />}
          >
            {t('gate.roster.update')}
          </Button>
        </div>
        {!online && <p className="text-[12px] text-muted">{t('gate.roster.needsNetwork')}</p>}
        {info.freshness === 'stale' && <Banner tone="warn">{t('gate.roster.stale')}</Banner>}
        {info.freshness === 'expired' && <Banner tone="error">{t('device.rosterExpired')}</Banner>}
        {error && <Banner tone="error">{error}</Banner>}
      </CardBody>
    </Card>
  )
}
